import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { sb } from "../lib/supabase";

interface SessionState {
  session: Session | null;
  loading: boolean;
  needsMfa: boolean;          // signed in, but two-step verification is on and not yet done this session
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionState>({ session: null, loading: true, needsMfa: false, refresh: async () => {} });

async function mfaPending(): Promise<boolean> {
  const { data, error } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return false;
  return data.nextLevel === "aal2" && data.currentLevel !== "aal2";
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsMfa, setNeedsMfa] = useState(false);

  const refresh = async () => {
    const { data } = await sb.auth.getSession();
    setSession(data.session);
    setNeedsMfa(data.session ? await mfaPending() : false);
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
    const { data } = sb.auth.onAuthStateChange(() => { void refresh(); });
    return () => data.subscription.unsubscribe();
  }, []);

  return <Ctx.Provider value={{ session, loading, needsMfa, refresh }}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);
